import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowDown, ArrowUp } from 'lucide-react';
import Checkbox from '../ui/Checkbox';
import Tooltip from '../ui/Tooltip';
import { controlResetClassName, cx } from '../ui/classNames';
import { SELECT_COLUMN_ID, getColumnLabel } from './columnLayout';
import {
    alignClassNames,
    fillerHeaderCellClassName,
    headerAlignClassNames,
    headerCellClassNames,
    headerLabelClassNames,
    headerRowClassName,
    lastPinnedClassName,
    sortButtonClassName,
    sortIconClassNames,
} from './tableClasses';

const ARIA_SORT = Object.freeze({ asc: 'ascending', desc: 'descending' });

// The tooltip of a header: its full label when the label is cut off, then
// its description.
function headerTooltip(label, description) {
    if (label && description) {
        return (
            <>
                <span className="block font-bold">{label}</span>
                <span className="block">{description}</span>
            </>
        );
    }
    return label || description || null;
}

// The leaf columns a header covers, as layout entries.
function coveredEntries(cell, layout) {
    if (cell.kind === 'leaf') return [layout.byId.get(cell.header.column.id)];
    return cell.header.column.getLeafColumns()
        .map((column) => layout.byId.get(column.id))
        .filter(Boolean);
}

// One header cell: a leaf column (sortable through its label button, with
// any `meta.headerControls` after it), a group spanning its leaf columns, a
// blank placeholder, or the select-all checkbox. A string label is cut off
// with an ellipsis (with `wrap`, after a second line); hovering or focusing
// it then shows the full label (and any `meta.description`) in a tooltip.
function HeaderCell({ cell, depth, layout, selectAll, wrap }) {
    const labelRef = useRef(null);
    const [truncated, setTruncated] = useState(false);
    const { header, kind, rowSpan, colSpan } = cell;
    const { column } = header;
    const { meta = {} } = column.columnDef;
    const entries = coveredEntries(cell, layout);
    const pinned = entries.length > 0 && entries.every((entry) => entry.pinned);
    const align = kind === 'leaf' ? entries[0].align : (meta.align || 'center');
    const style = {
        top: `calc(var(--tbl-head-h) * ${depth})`,
        left: pinned ? entries[0].stickyLeft : undefined,
    };
    const className = cx(
        pinned ? headerCellClassNames.pinned : headerCellClassNames.plain,
        pinned && entries[entries.length - 1].lastPinned && lastPinnedClassName,
        meta.headerClassName,
    );
    const cellProps = {
        rowSpan: rowSpan > 1 ? rowSpan : undefined,
        colSpan: colSpan > 1 ? colSpan : undefined,
        className,
        style,
    };

    if (kind === 'blank') return <th aria-hidden="true" {...cellProps} />;
    if (column.id === SELECT_COLUMN_ID) {
        return (
            <th scope="col" data-field={column.id} {...cellProps}>
                <div className="flex justify-center">
                    <Checkbox aria-label="Select all rows" {...selectAll} />
                </div>
            </th>
        );
    }

    const content = typeof column.columnDef.header === 'function'
        ? column.columnDef.header(header.getContext())
        : column.columnDef.header;
    const textLabel = typeof content === 'string';
    const checkTruncation = () => {
        const element = labelRef.current;
        setTruncated(Boolean(element) && (
            element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight
        ));
    };
    const label = (
        <span
            ref={textLabel ? labelRef : undefined}
            className={cx(
                'min-w-0',
                textLabel && headerLabelClassNames[wrap ? 'wrap' : 'truncate'],
                textLabel && wrap && alignClassNames[align],
            )}
        >
            {content}
        </span>
    );
    const tooltip = headerTooltip(
        truncated ? getColumnLabel(column.columnDef, column.id) : null,
        meta.description,
    );
    const sortable = kind === 'leaf' && column.getCanSort();
    const sortDirection = kind === 'leaf' ? column.getIsSorted() : false;
    // Right-aligned columns lead with the icon, so labels line up on the right.
    const reverse = align === 'right';
    const centered = align === 'center';
    let title;
    if (sortable) {
        // The direction shown; an unsorted column previews its first one.
        const firstDirection = column.getFirstSortDir();
        const SortIcon = (sortDirection || firstDirection) === 'desc' ? ArrowDown : ArrowUp;
        title = (
            <button
                type="button"
                className={cx(
                    controlResetClassName,
                    sortButtonClassName,
                    reverse && 'flex-row-reverse',
                    centered && 'justify-center',
                )}
                onClick={() => column.toggleSorting()}
                onMouseOver={checkTruncation}
                onFocus={checkTruncation}
            >
                {label}
                <SortIcon
                    aria-hidden="true"
                    size={14}
                    strokeWidth={2.25}
                    className={sortDirection ? sortIconClassNames.sorted : sortIconClassNames.unsorted}
                />
            </button>
        );
    } else {
        title = (
            <span
                className={cx('flex min-w-0', reverse && 'justify-end')}
                onMouseOver={checkTruncation}
            >
                {label}
            </span>
        );
    }

    return (
        <th
            scope={kind === 'group' ? 'colgroup' : 'col'}
            data-field={column.id}
            aria-sort={sortable ? (ARIA_SORT[sortDirection] || 'none') : undefined}
            {...cellProps}
        >
            <div className={cx('flex min-w-0 items-center gap-1', headerAlignClassNames[align])}>
                <Tooltip content={tooltip}>{title}</Tooltip>
                {meta.headerControls?.(header.getContext())}
            </div>
        </th>
    );
}

HeaderCell.propTypes = {
    cell: PropTypes.object.isRequired,
    depth: PropTypes.number.isRequired,
    layout: PropTypes.object.isRequired,
    selectAll: PropTypes.object,
    wrap: PropTypes.bool,
};

/**
 * The <thead>: one row per TanStack header group, sticky at the top of the
 * frame, plus the filler column's header cell when the layout has one.
 * `wrap` lets labels take two lines (DataTable `wrapHeaders`).
 */
export default function DataTableHeader({ headerRows, layout, selectAll, wrap = false }) {
    return (
        <thead>
            {headerRows.map((headerRow) => (
                <tr key={headerRow.id} className={headerRowClassName}>
                    {headerRow.cells.map((cell) => (
                        <HeaderCell
                            key={cell.header.id}
                            cell={cell}
                            depth={headerRow.depth}
                            layout={layout}
                            selectAll={selectAll}
                            wrap={wrap}
                        />
                    ))}
                    {headerRow.depth === 0 && layout.filler && (
                        <td
                            aria-hidden="true"
                            rowSpan={headerRows.length > 1 ? headerRows.length : undefined}
                            className={fillerHeaderCellClassName}
                            style={{ top: 0 }}
                        />
                    )}
                </tr>
            ))}
        </thead>
    );
}

DataTableHeader.propTypes = {
    headerRows: PropTypes.array.isRequired,
    layout: PropTypes.object.isRequired,
    selectAll: PropTypes.object,
    wrap: PropTypes.bool,
};
